import { useStaticQueryDataChatMutuo } from "../../query/useStaticQueryDataChatMutuo";
import { useStaticQueryDataMenuDesktop } from "../../query/useStaticQueryDataMenuDesktop";
import {
  HIDE_MODAL_CONTATTACI,
  IS_MEGA_MENU_OPEN,
  SHOW_MODAL_CONTATTACI,
} from "../../state/actions/ui.actions";
import { getContattaciShow } from "../../state/selectors/ui.selector";
import { flatMenuListToHierarchical, removePTag } from "../../utils/helpers";
import {
  useDesktopMediaQuery,
  useOnlyLgMediaQuery,
} from "../ComponentUtils/Responsive";
import Forms from "../FlexibleComponents/Forms";
import Image from "../Image";
import ModalService from "../ModalService";
import UniversalLink from "../UniversalLink";
import * as menuDesktopStyles from "./menu-desktop.module.scss";
import { useLocation } from "@reach/router";
import classNames from "classnames";
import { motion } from "framer-motion";
import parse from "html-react-parser";
import React, { useEffect, useLayoutEffect, useState, useRef, useMemo } from "react";
import { useActiveSection } from "../../utils/hooks/useActiveSection";
import { Col, Container, Nav, Navbar, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";

const numeroVerdeVariants = {
  open: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
    },
  },
  close: {
    opacity: 0,
    y: -34,
    transition: {
      duration: 0.4,
    },
  },
};

const logoVariants = {
  open: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
    },
  },
  close: {
    opacity: 0,
    y: -30,
    transition: {
      duration: 0.4,
    },
  },
};

const MenuDesktop = ({
  isHpTemplate = false,
  lightVersion = false,
  isChatMutuoTemplate = false,
}) => {
  const dispatch = useDispatch();
  const isDesktop = useDesktopMediaQuery();
  const [expanded, setExpanded] = useState("");
  const [isShrunk, setShrunk] = useState(false);
  const showModalContattaci = useSelector(getContattaciShow);
  const [parentId, setParentId] = useState(null);
  const [blockCurrentPath, setBlockCurrentPath] = useState(false); // blocca il current path se siamo in un menu figlio (CONTROLLA LA DUPPLICAZIONE DI VOCI DI MENU ATTIVE)
  const headerRef = useRef(null);
  const {
    logo,
    numeroVerde,
    ctaNumeroVerde,
    logoMenu,
    menu,
  } = useStaticQueryDataMenuDesktop();

  const {
    chatMutuo: { logo: logoChatMutuo, disattivazioneTotaleChatMutuo },
  } = useStaticQueryDataChatMutuo();

  const isChatMutuoDisabledType = (templateName) =>
    disattivazioneTotaleChatMutuo &&
    (templateName === "ChatMutuo" || templateName === "Landing Page");

  useEffect(() => {
    if (!isDesktop) {
      dispatch(HIDE_MODAL_CONTATTACI());
    }
  }, [isDesktop]);

  const location = useLocation();

  // Aggiorna --header-offset su <html> con l'altezza reale del navbar fisso.
  // Permette a scroll-margin-top delle sezioni con ancora di adattarsi
  // automaticamente a mobile/tablet/desktop e allo stato shrunk.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const update = () => {
      const h = headerRef.current?.getBoundingClientRect().height;
      if (h) {
        document.documentElement.style.setProperty("--header-offset", `${Math.round(h) + 8}px`);
      }
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [isShrunk]);

  // Espone --header-offset-full e --header-offset-shrunk misurando l'altezza
  // del navbar in entrambi gli stati (toggle sincrono della classe scrolled).
  // Permette a scrollToAnchor in gatsby-browser di predire l'offset corretto
  // post-scroll, evitando che le ancore atterrino con un gap sotto la navbar
  // shrunk quando si naviga a freddo verso un #anchor.
  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    // headerRef è sul <header> wrapper (height 0 perché Navbar è position:fixed
    // ed esce dal flow). L'altezza reale è sul <Navbar> figlio.
    const el = headerRef.current?.firstElementChild;
    if (!el) return;

    const measure = () => Math.round(el.getBoundingClientRect().height) + 8;
    const scrolledClass = menuDesktopStyles.menuDesktopScrolled;
    if (!scrolledClass) return;
    const hadClass = el.classList.contains(scrolledClass);

    const currentH = measure();
    if (hadClass) el.classList.remove(scrolledClass);
    else el.classList.add(scrolledClass);
    // Forza reflow per leggere la nuova height dopo il toggle
    // eslint-disable-next-line no-unused-expressions
    el.offsetHeight;
    const oppositeH = measure();
    if (hadClass) el.classList.add(scrolledClass);
    else el.classList.remove(scrolledClass);
    // eslint-disable-next-line no-unused-expressions
    el.offsetHeight;

    const fullH = hadClass ? oppositeH : currentH;
    const shrunkH = hadClass ? currentH : oppositeH;
    document.documentElement.style.setProperty("--header-offset-full", `${fullH}px`);
    document.documentElement.style.setProperty("--header-offset-shrunk", `${shrunkH}px`);
  }, [isShrunk]);

  useEffect(() => {
    const onScroll = () => {
      const scrollTop = document.documentElement.scrollTop;
      setShrunk(scrollTop > 25);
      // se lo scroll è attivo, e siamo nella stessa pagina, si attiva setExpanded a null
      if (!location.key) {
        setExpanded(null);
      }
    };
    if (location.pathname === "/") {
      setParentId(null);
    }

    if (parentId) {
      setBlockCurrentPath(true);
    }

    // fuori di onScroll, si attiva se cambiamo pagina (dependency location.key)
    setExpanded(null);
    dispatch(HIDE_MODAL_CONTATTACI());
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.key]);

  // Deep-link `?contatto=open`: apre il modale Contattaci ESISTENTE (usato
  // dall'estensione ChatMutuo, riutilizzabile da campagne/link esterni).
  // DEVE stare DOPO l'effect [location.key] qui sopra: quello chiude il modale al
  // mount/navigazione, quindi per l'ordine di esecuzione degli effect il nostro
  // SHOW dev'essere l'ultimo dispatch, altrimenti l'HIDE lo sovrascrive.
  // Il modale è desktop-only (come tutto MenuDesktop): niente apertura su mobile.
  // Ripulisce il param dall'URL (replaceState dentro l'effect, come page-chatmutuo.jsx)
  // per evitare la riapertura su reload/back.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isDesktop) return;
    const params = new URLSearchParams(location.search);
    if (params.get("contatto") !== "open") return;
    dispatch(SHOW_MODAL_CONTATTACI());
    params.delete("contatto");
    const query = params.toString();
    const newUrl = location.pathname + (query ? `?${query}` : "") + location.hash;
    window.history.replaceState({}, document.title, newUrl);
  }, [location.search, location.pathname, location.hash, isDesktop, dispatch]);

  const hierarchicalList = flatMenuListToHierarchical(menu.nodes) || [];

  const allAnchorIds = useMemo(
    () => hierarchicalList.flatMap(item => [
      item?.acfAncoraPagina?.paginaAncoraId,
      ...(item.children || []).map(c => c?.acfAncoraPagina?.paginaAncoraId),
    ]).filter(Boolean),
    [hierarchicalList]
  );
  const activeSection = useActiveSection(allAnchorIds);

  const isOnlyLg = useOnlyLgMediaQuery();

  const MenuConditional = ({
    templateName,
    label,
    novitaClass,
    etichetta = false,
    testoEtichetta = "",
    beta = false
  }) => {
    const betaLabel = (beta) ? (<><sup style={{
              fontSize: ".5625rem",
              fontWeight: "bold",
            }}> BETA</sup></>) : ("");

    if (templateName === "ChatMutuo" || templateName === "Landing Page") {
      return (
        <>
          <span>
            <span
              style={{
                color: "#72B043",
              }}
            >
              Chat
            </span>
            <span
              style={{
                color: "#468A4C",
              }}
            >
              Mutuo AI
            </span>
          </span>
          <sup
            style={{
              color: "#F37324",
              fontSize: ".5625rem",
              fontWeight: "bold",
            }}
          > BETA
          </sup>
        </>
      );
    } else 
    {
        if (novitaClass) {
        return (
          <>
            <span
              style={{
                paddingRight: "5px",
              }}
            >
              {etichetta && testoEtichetta && isOnlyLg ? null : (
                <img
                  src="/megafono.svg"
                  alt="megafono icon"
                  width={15}
                  height={15}
                />
              )}
            </span>
            <span>
              {etichetta && testoEtichetta && isOnlyLg ? testoEtichetta : label}
              {/* NOVITÀ */}
            </span>
          </>
        );
      } else if (etichetta && testoEtichetta && isOnlyLg) {
        return (<><span> {testoEtichetta} </span> {betaLabel}</>);
      } else {
        return (<><span> {label} </span> {betaLabel}</>);
      }
    }
    
  };

  const [hoverItem, setHoverItem] = useState(null);
  const [isMenuChildOpen, setMenuChildOpen] = useState(null);
  const [activeMenuItem, setActiveMenuItem] = useState(null);
  const hoverTimeoutRef = useRef();
  const menuHoverRef = useRef();
  const megaMenuHoveredRef = useRef();
  
  const handleMenuItemClick = (id) => {
    setActiveMenuItem(id);
  };

  useEffect(() => {
    dispatch(IS_MEGA_MENU_OPEN(isMenuChildOpen ? true : false));
  }, [isMenuChildOpen]);

  return (
    <header ref={headerRef}>
      <Navbar
        fixed="top"
        className={classNames(menuDesktopStyles.menuDesktop, {
          [menuDesktopStyles.menuDesktopScrolled]: isShrunk,
          [menuDesktopStyles.menuDesktopHideMobile]: isHpTemplate,
          [menuDesktopStyles.menuChatMutuo]: isChatMutuoTemplate,
        })}
      >
        <Container
          className={classNames(menuDesktopStyles.menuDesktopContainer, {
            [menuDesktopStyles.menuDesktopContainerChatMutuo]: isChatMutuoTemplate,
          })}
        >
          <Navbar.Brand href="/">
            <Image
              data={logo}
              className={classNames(menuDesktopStyles.menuDesktopBrandLogo, {
                [menuDesktopStyles.menuDesktopLogoChatMutuo]: isChatMutuoTemplate,
              })}
            />

            {isChatMutuoTemplate && (
              <Image
                data={logoChatMutuo}
                className={classNames({
                  [menuDesktopStyles.menuMobileChatMutuo]: isChatMutuoTemplate,
                })}
              />
            )}
          </Navbar.Brand>
          {hierarchicalList?.length > 0 && !lightVersion && (
            <Nav
              className={classNames(menuDesktopStyles.menuDesktopMenu, {
                [menuDesktopStyles.menuDesktopMenuScrolled]: isShrunk,
              })}
            >
              {hierarchicalList.map((menuItem) => {
                const { id, target, path, label, children } = menuItem;
                const templateName =
                  menuItem?.connectedNode?.node?.template?.templateName || "";
                if (isChatMutuoDisabledType(templateName)) return null;
                const hasMenuNovita = menuItem?.acfMenuNovita?.menuNovita;
                const isBeta = menuItem?.acfMenuBeta?.beta;
                const hasEtichettaCorta =
                  menuItem?.acfEtichettaCorta?.etichettaCorta;
                const nuovaEtichetta =
                  menuItem?.acfEtichettaCorta?.nuovaEtichetta;
                const ancoraTarget = menuItem?.acfAncoraPagina?.paginaAncoraTarget?.uri;
                const ancoraId = menuItem?.acfAncoraPagina?.paginaAncoraId;
                const baseTarget = ancoraTarget ?? path;
                const finalPath = ancoraId
                  ? `${baseTarget.replace(/\/+$/, '')}/#${ancoraId}`
                  : path;
                const matchOfId = parentId === id;

                const basePath = (ancoraTarget || path).replace(/\/?$/, '/');
                const isCurrentPath = ancoraId
                  ? location.pathname.replace(/\/?$/, '/') === basePath && activeSection === ancoraId
                  : !blockCurrentPath && location.pathname === path;
                const clearParentId = () => {
                  setParentId(null);
                  setBlockCurrentPath(false);
                };
                return (
                  <div key={id}>
                    <Nav.Link
                      onClick={() => {
                        clearParentId();
                        setExpanded(null);
                        setMenuChildOpen(null);
                      }}
                      as={UniversalLink}
                      to={finalPath}
                      target={target}
                      onMouseEnter={() => {
                        menuHoverRef.current = true;
                        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
                        hoverTimeoutRef.current = setTimeout(() => {
                          if (menuHoverRef.current && children.length > 0) {
                            setExpanded(id);
                            setHoverItem(id);
                            setMenuChildOpen(id);
                          } else {
                          setExpanded(null);
                          setHoverItem(id);
                          setMenuChildOpen(null);
                        }
                        }, 80);
                      }}
                      onMouseLeave={() => {
                        menuHoverRef.current = false;
                        // Delay la chiusura per permettere il passaggio al mega menu
                        hoverTimeoutRef.current = setTimeout(() => {
                          if (!menuHoverRef.current) {
                            setHoverItem(null);
                            setExpanded(null);
                            setMenuChildOpen(null);
                          }
                        }, 100); // 100ms di tolleranza per il passaggio mouse
                      }}
                      className={classNames({
                        [menuDesktopStyles.activeMenuItem]:
                          activeMenuItem === id ||
                          hoverItem === id ||
                          isCurrentPath ||
                          matchOfId ||
                          isMenuChildOpen === id,
                      })}
                      style={{ padding: "0" }}
                    >
                      <div>
                        <MenuConditional
                          templateName={templateName}
                          label={label}
                          novitaClass={hasMenuNovita}
                          etichetta={hasEtichettaCorta}
                          testoEtichetta={nuovaEtichetta}
                          beta={isBeta}
                        />
                      </div>
                    </Nav.Link>
                  </div>
                );
              })}
            </Nav>
          )}
          {numeroVerde && isHpTemplate && !lightVersion && (
            <motion.button
              className={menuDesktopStyles.fixedNumber}
              variants={numeroVerdeVariants}
              initial="close"
              animate={isShrunk ? "close" : "open"}
              transition={{ duration: 0.4 }}
              onClick={() =>
                ctaNumeroVerde.popup
                  ? dispatch(SHOW_MODAL_CONTATTACI())
                  : window.open(`tel:+39 ${numeroVerde}`, "_blank")
              }
            >
              {numeroVerde}
            </motion.button>
          )}
          {!isHpTemplate && logoMenu && (
            <motion.div
              className={menuDesktopStyles.fixedLogo}
              variants={logoVariants}
              initial="close"
              animate={isShrunk ? "close" : "open"}
              transition={{ duration: 0.4 }}
            >
              <Image data={logoMenu} useImgTag />
            </motion.div>
          )}
        </Container>
      </Navbar>
      {hierarchicalList?.length > 0 &&
        !lightVersion &&
        hierarchicalList?.map((menuItem) => {
          const { children, id, beta } = menuItem;
          const parentTemplateName = menuItem?.connectedNode?.node?.template?.templateName || "";
          if (isChatMutuoDisabledType(parentTemplateName)) return null;

          if (children?.length > 0) {
            return (
              <motion.div
                onClick={() => handleMenuItemClick(children.parentId)}
                onMouseEnter={() => {
                  megaMenuHoveredRef.current = true; // mouse sopra
                  if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
                  hoverTimeoutRef.current = setTimeout(() => {
                    if (megaMenuHoveredRef.current) { // controllo realtime
                      setExpanded(id);
                      setHoverItem(id);
                      setMenuChildOpen(id);
                    }
                  }, 100);
                }}
                onMouseLeave={() => {
                  megaMenuHoveredRef.current = false; // mouse fuori
                  if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
                  setExpanded(null);
                  setHoverItem(null);
                  setMenuChildOpen(null);
                }}
                initial="close"
                animate={expanded === id ? "open" : "close"}
                variants={{
                  open: {
                    display: "block",
                    height: "auto",
                    transition: {
                      duration: 0.5,
                      ease: [0.55, 0.06, 0.68, 0.19], // slow start, fast end
                      delay: 0.0
                    },
                  },
                  close: {
                    height: 0,
                    transitionEnd: {
                      display: "none",
                    },
                    transition: {
                      duration: 0.22,
                      ease: [0.55, 0.06, 0.68, 0.19],
                      delay: 0.0,
                    },
                  },
                }}
                className={classNames(menuDesktopStyles.megaMenu, {
                  [menuDesktopStyles.megaMenuScrolled]: isShrunk,
                })}
                key={`${id}-mega-menu`}
              >
                {/* White strip for blending with background */}
                <div className={menuDesktopStyles.megaMenuRowBlank}></div>
                <Container className={menuDesktopStyles.megaMenuContainer}>
                  <Row className={menuDesktopStyles.megaMenuRow}>
                    <Col lg={5}>
                      <div className={menuDesktopStyles.megaMenuTitleWrapper}>
                        <h2>
                          {parse(removePTag(menuItem.label))}
                          <img
                            src="/megamenu.svg"
                            alt="megamenu background icon"
                            width={150}
                            height={150}
                          />
                        </h2>
                      </div>
                    </Col>
                    <Col lg={7}>
                      <ul
                        className={classNames(
                          menuDesktopStyles.megaMenuItemsWrapper
                        )}
                      >
                        {children.map((menuItem) => {
                          const { id, path, label, parentId, acfMenuBeta } = menuItem;
                          const childTemplateName = menuItem?.connectedNode?.node?.template?.templateName || "";
                          if (isChatMutuoDisabledType(childTemplateName)) return null;
                          const childAncoraTarget = menuItem?.acfAncoraPagina?.paginaAncoraTarget?.uri;
                          const childAncoraId = menuItem?.acfAncoraPagina?.paginaAncoraId;
                          const childBaseTarget = childAncoraTarget ?? path;
                          const childFinalPath = childAncoraId
                            ? `${childBaseTarget.replace(/\/+$/, '')}/#${childAncoraId}`
                            : path;

                          const handleParentId = (parentId) => {
                            setParentId(parentId);
                          };

                          const childBasePath = (childAncoraTarget || path).replace(/\/?$/, '/');
                          const isCurrentPath = childAncoraId
                            ? location.pathname.replace(/\/?$/, '/') === childBasePath && activeSection === childAncoraId
                            : location.pathname === path;
                          const isBeta = acfMenuBeta?.beta;

                          return (
                            <li
                              key={id}
                              className={classNames({
                                [menuDesktopStyles.activeChild]: isCurrentPath,
                              })}
                            >
                              <UniversalLink
                                to={childFinalPath}
                                onClick={() => handleParentId(parentId)}
                              >
                                {label}
                              </UniversalLink>
                              {isBeta ? (<> <sup style={{
                                color: "#fff",
                                fontSize: ".5625rem",
                                fontWeight: "bold",
                              }}>BETA</sup></>) : ("")}
                            </li>
                          );
                        })}
                      </ul>
                    </Col>
                  </Row>
                </Container>
              </motion.div>
            );
          }
        })}
      <ModalService
        show={showModalContattaci}
        setShow={() => dispatch(HIDE_MODAL_CONTATTACI())}
        title={ctaNumeroVerde.popup.titolo}
        modalBodyClassName={menuDesktopStyles.modalBody}
      >
        <div className={menuDesktopStyles.modalForm}>
          <Forms
            noContainer
            layoutData={{
              fieldGroupName: "Forms",
              form: ctaNumeroVerde.popup.form,
              labelCta: "Invia",
              sottotitolo: ctaNumeroVerde.popup.sottotitolo,
              consenso: ctaNumeroVerde.popup.consenso,
            }}
          />
        </div>
      </ModalService>
    </header>
  );
};

export default MenuDesktop;
